import { useCallback } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import { TruncatedTextTooltip } from "components/common/TruncatedTextTooltip";
import { useAutoScrollToRef } from "hooks/useAutoScrollToRef";
import type { SelectPortalMenuProps } from "./types";

export const SelectPortalMenu = ({
  isOpen,
  position,
  filteredOptions,
  highlightedValue,
  onSelect,
  menuClassName,
  menuRef,
}: SelectPortalMenuProps) => {
  const { t } = useTranslation();

  const { containerRef, targetRef } = useAutoScrollToRef<
    HTMLDivElement,
    HTMLButtonElement
  >({
    resetDep: highlightedValue,
    options: { block: "center" },
  });

  const setMenuNode = useCallback(
    (node: HTMLDivElement | null) => {
      menuRef.current = node;
      containerRef.current = node;
    },
    [menuRef, containerRef],
  );

  if (!isOpen || !position) return null;

  return createPortal(
    <div
      ref={setMenuNode}
      className={clsx(
        "absolute z-50 rounded-md border border-gray-200 bg-white shadow-lg",
        "max-h-60 overflow-auto",
        menuClassName,
      )}
      style={{
        top: `${position.top}px`,
        left: `${position.left}px`,
        width: `${position.width}px`,
        transform:
          position.placement === "top" ? "translateY(-100%)" : undefined,
      }}
      onMouseDown={(e) => e.preventDefault()}
    >
      {filteredOptions.length === 0 ? (
        <div className="px-3 py-2 text-xs text-gray-500">
          {t("common.noOptions")}
        </div>
      ) : (
        filteredOptions.map((option) => {
          const isHighlighted = option.value === highlightedValue;
          return (
            <button
              key={option.value}
              ref={isHighlighted ? targetRef : undefined}
              type="button"
              disabled={option.disabled}
              onClick={() => onSelect(option)}
              className={clsx(
                "w-full text-left px-3 py-2 text-sm transition",
                "border-l-4 border-l-transparent",
                "flex items-center gap-2",
                option.disabled && "opacity-50 cursor-not-allowed",
                isHighlighted
                  ? "bg-blue-100 border-l-blue-600 text-blue-700 font-medium"
                  : "hover:bg-gray-50",
              )}
            >
              <TruncatedTextTooltip
                text={option.label}
                className="min-w-0 flex-1"
              />
              {option.endAdornment}
            </button>
          );
        })
      )}
    </div>,
    document.body,
  );
};
